/**
 * Yönetim paneli kabuğu — M8.
 *
 * HesapSis düzeni: sıcak uygulama zemini üzerinde yuvarlak kartlar,
 * ince kenarlıklar ve yumuşak gölgeler. Okul seçici, nokta süzgeci,
 * kimlik ve tema sabit kenar çubuğunda; içerik bağımsız kaydırılır.
 *
 * Kabuğun üç işi değişmedi:
 *   1) OTURUM: giriş yoksa giriş ekranı; 401 gelirse oturumu düşürmek.
 *   2) KAPSAM: OKUL SEÇİCİ (KK-801) ve NOKTA SÜZGECİ. Okul değişimi önbelleği
 *      temizler — bir okulun cirosu diğerinin ekranında görünemez.
 *   3) YÖN: 240px kenar çubuğu. Masaüstü önce (1280px+), 768px'te de çalışır;
 *      küçük ekranda kenar çubuğu yatay bir şeride toplanır ve kapsam
 *      seçicileri ince bir üst banda çıkar.
 *
 * Yazdırma: kabuk `print:hidden`dir. Z raporu ve kâr-zarar çıktısında kâğıda
 * menü basmak yalnızca mürekkep israfı değil, okunması gereken tabloyu
 * küçültmek demektir.
 */
import { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Rol,
  STOK_YETKILI_ROLLER,
  TUM_ROLLER,
  YUKLEME_ROLLERI,
  VELI_GIRIS_AKTARIM_ROLLERI,
} from "@kantin/shared";
import { IkonButon } from "@kantin/ui";
import type { IkonBileseni } from "@kantin/ui";
import {
  IkonAnomali,
  IkonArti,
  IkonAy,
  IkonAyarlar,
  IkonCari,
  IkonCikis,
  IkonDenetimKaydi,
  IkonEkran,
  IkonGelirGider,
  IkonGosterge,
  IkonGunes,
  IkonIceAktarim,
  IkonImha,
  IkonKarZarar,
  IkonKarantina,
  IkonKart,
  IkonKisiler,
  IkonMalKabul,
  IkonMutabakat,
  IkonNokta,
  IkonNoktaSorumluluk,
  IkonPersonel,
  IkonSayim,
  IkonSevron,
  IkonStok,
  IkonSuzgec,
  IkonTerminal,
  IkonTransfer,
  IkonUrun,
  IkonUrunKarlilik,
  IkonYuklemeMutabakati,
  IkonYuklemeOnay,
  IkonZRaporu,
} from "@kantin/ui";
import { apiOku, OTURUM_DUSTU } from "./veri/api.js";
import { OkulLogosu } from "@kantin/ui";
import {
  aktifOturum,
  baskaSekmeDinle,
  oturumDurumu,
  temayiUygula,
} from "./veri/oturum.js";
import type { Tema } from "./veri/oturum.js";
import { useNoktalar, useOzet } from "./veri/sorgular.js";
import { Giris } from "./Giris.js";
import { YonetimMarkasi } from "./YonetimMarkasi.js";
import { Gosterge } from "./ekranlar/gosterge/Gosterge.js";
import { Mutabakat } from "./ekranlar/mutabakat/Mutabakat.js";
import { Anomali } from "./ekranlar/mutabakat/Anomali.js";
import { TerminalSagligi } from "./ekranlar/terminal/TerminalSagligi.js";
import { ZRaporu } from "./ekranlar/rapor/ZRaporu.js";
import { KarZararRaporu } from "./ekranlar/rapor/KarZararRaporu.js";
import { UrunKarlilik } from "./ekranlar/rapor/UrunKarlilik.js";
import { NoktaSorumluluk } from "./ekranlar/rapor/NoktaSorumluluk.js";
import { Audit } from "./ekranlar/rapor/Audit.js";
import { MalKabulGecmisi, MalKabulDetay } from "./ekranlar/yonetim/MalKabulGecmisi.js";
import { VeliGirisBilgileri } from "./ekranlar/yonetim/VeliGirisBilgileri.js";
// Tanım ve işletme ekranları ayrı bir modülde yaşar ve kendi verisini kendi
// çeker; kabuk yalnızca rotalarını bağlar (bkz. ekranlar/yonetim/index.ts).
import {
  Cari,
  GelirGider,
  IceAktarim,
  Imha,
  Karantina,
  Kartlar,
  Kisiler,
  MalKabul,
  Noktalar,
  OkulAyarlari,
  Personel,
  Sayim,
  Stok,
  Transferler,
  Urunler,
  YuklemeMutabakati,
  YuklemeOnay,
} from "./ekranlar/yonetim/index.js";

type Grup =
  | "İzleme"
  | "Denetim"
  | "Raporlar"
  | "Kişi ve kart"
  | "Stok"
  | "Muhasebe"
  | "Tanımlar";

interface YonOgesi {
  yol: string;
  metin: string;
  grup: Grup;
  /**
   * İkon METNİN YERİNE GEÇMEZ, yanında durur (WCAG 1.4.1). İşi, 23 kalemlik
   * bir listede gözün satır satır okumak yerine ŞEKİLDEN sıçrayabilmesidir;
   * dar ekranda etiketin altında değil üstünde olması da bundandır.
   */
  ikon: IkonBileseni;
  /** Dar ekranda alt şeritte görünen kısa ad — iki kelimeyi taşıyamaz. */
  kisa?: string;
  /**
   * Ekranın OKUMA yetkisi olan roller (§5.1). Verilmezse herkese açıktır.
   * Burada listelenmeyen bir rol için sunucu GET'i de reddeder; menüde
   * bırakmak kullanıcıyı yalnızca "yetkiniz yok" duvarına götürür.
   * Yazma yetkisi bu listeyle KARIŞTIRILMAZ — o, ekranın kendi içindedir.
   */
  roller?: number[];
}

/** Kâr-zarar, cari ve gelir/gider uçları MALI_ROLLER ile korunur (§5.1). */
const MALI: number[] = [
  Rol.KANTIN_MUDURU,
  Rol.OKUL_SISTEM_YONETICISI,
  Rol.MALI_MUSAVIR,
];

/**
 * Mali müşavirin işi olmayan ekranlar.
 *
 * Menü bir GÜVENLİK katmanı değildir; buradaki amaç, mali müşaviri sunucunun
 * zaten reddedeceği kişi/kart ve tanım ekranlarına ya da defterine yazmadığı
 * stok operasyonuna davet etmemektir. Küme "6 hariç herkes" diye yazılır ki
 * mevcut rollerin gördüğü menü BİRE BİR aynı kalsın.
 */
const MALI_MUSAVIR_HARIC: number[] = TUM_ROLLER.filter(
  (r) => r !== Rol.MALI_MUSAVIR,
);

/**
 * Sunucunun YONETIM_OKUMA kümesi (yonetim.rotalari.ts): mutabakat, anomali
 * ve denetim kaydı bu rollere açıktır. Nokta görevlisi (rol 2) bu kümede
 * DEĞİLDİR; menüde gösterilirse boş ekran görür.
 */
const YONETIM_OKUMA: number[] = [
  Rol.KANTIN_MUDURU,
  Rol.OKUL_YONETIMI,
  Rol.OKUL_SISTEM_YONETICISI,
  Rol.MALI_MUSAVIR,
];

/**
 * Sunucunun IZLEME_OKUMA kümesi: gösterge ve terminal sağlığı nokta
 * görevlisine de açıktır — sunucu yanıtı atandığı noktalarla süzer.
 */
const IZLEME_OKUMA: number[] = [...YONETIM_OKUMA, Rol.NOKTA_SORUMLUSU];

/** Kişi/kart/tanım ekranları: sunucu nokta görevlisine 403 döner. */
const GOREVLI_VE_MUSAVIR_HARIC: number[] = MALI_MUSAVIR_HARIC.filter(
  (r) => r !== Rol.NOKTA_SORUMLUSU,
);

const YON: YonOgesi[] = [
  {
    yol: "/",
    metin: "Gösterge",
    grup: "İzleme",
    ikon: IkonGosterge,
    roller: IZLEME_OKUMA,
  },
  {
    yol: "/terminal",
    metin: "Terminaller",
    grup: "İzleme",
    ikon: IkonTerminal,
    roller: IZLEME_OKUMA,
    kisa: "Terminal",
  },
  {
    yol: "/mutabakat",
    metin: "Mutabakat",
    grup: "Denetim",
    ikon: IkonMutabakat,
    roller: YONETIM_OKUMA,
  },
  {
    yol: "/anomali",
    metin: "Anomali",
    grup: "Denetim",
    ikon: IkonAnomali,
    roller: YONETIM_OKUMA,
  },
  {
    yol: "/audit",
    metin: "Denetim kaydı",
    grup: "Denetim",
    ikon: IkonDenetimKaydi,
    roller: YONETIM_OKUMA,
    kisa: "Denetim",
  },
  { yol: "/rapor/z", metin: "Z raporu", grup: "Raporlar", ikon: IkonZRaporu },
  {
    yol: "/rapor/kar-zarar",
    metin: "Kâr-zarar",
    grup: "Raporlar",
    ikon: IkonKarZarar,
    roller: MALI,
  },
  {
    yol: "/rapor/urun-karlilik",
    metin: "Ürün kârlılığı",
    grup: "Raporlar",
    ikon: IkonUrunKarlilik,
    kisa: "Kârlılık",
    roller: MALI,
  },
  {
    yol: "/rapor/nokta-sorumluluk",
    metin: "Nokta sorumluluğu",
    grup: "Raporlar",
    ikon: IkonNoktaSorumluluk,
    kisa: "Sorumluluk",
    roller: MALI_MUSAVIR_HARIC,
  },
  {
    yol: "/kisi",
    metin: "Kişiler",
    grup: "Kişi ve kart",
    ikon: IkonKisiler,
    roller: GOREVLI_VE_MUSAVIR_HARIC,
  },
  {
    yol: "/kart",
    metin: "Kartlar",
    grup: "Kişi ve kart",
    ikon: IkonKart,
    roller: GOREVLI_VE_MUSAVIR_HARIC,
  },
  {
    yol: "/veli-giris-bilgileri",
    metin: "Veli giriş bilgileri",
    grup: "Kişi ve kart",
    ikon: IkonKisiler,
    kisa: "Veli bilgileri",
    roller: VELI_GIRIS_AKTARIM_ROLLERI,
  },
  {
    yol: "/ice-aktarim",
    metin: "İçe aktarım",
    grup: "Kişi ve kart",
    ikon: IkonIceAktarim,
    kisa: "Aktarım",
    roller: GOREVLI_VE_MUSAVIR_HARIC,
  },
  {
    yol: "/urun",
    metin: "Ürünler",
    grup: "Stok",
    ikon: IkonUrun,
    roller: MALI_MUSAVIR_HARIC,
  },
  {
    yol: "/stok",
    metin: "Stok",
    grup: "Stok",
    ikon: IkonStok,
    roller: MALI_MUSAVIR_HARIC,
  },
  // Mal kabul ve Sayım'da `roller` istisnaen YAZMA kümesiyle doldurulur: Mal
  // kabul'ün okunacak bir listesi yoktur, Sayım'ın okuma uçları da (GET /sayim)
  // zaten aynı kümeyle korunur. Gizlenen tek rol KASİYER ve o ikisinde de
  // hiçbir şey okuyamaz — menüye koymak onu yalnızca duvara götürürdü.
  //
  // İmha AYRIDIR ve burada listelenmez: okuduğu tek kaynak GET /stok'tur ve o uç
  // rol kısıtsızdır. Malı imhaya ayıran nokta sorumlusudur; "ayırdığım mal
  // tutanağa bağlandı mı" sorusu tam da onundur. Ekranı ondan gizlemek
  // okuyabildiği bir listeyi saklamak olurdu — yazma yüzeyi Imha.tsx içinde
  // IMHA_YETKILI_ROLLER ile kapatılır (KK-528).
  {
    yol: "/stok-giris",
    metin: "Mal kabul",
    grup: "Stok",
    ikon: IkonMalKabul,
    kisa: "Kabul",
    roller: STOK_YETKILI_ROLLER,
  },
  {
    yol: "/mal-kabul",
    metin: "Mal kabul geçmişi",
    grup: "Stok",
    ikon: IkonMalKabul,
    kisa: "Kabul geçmişi",
    roller: [...STOK_YETKILI_ROLLER, Rol.MALI_MUSAVIR],
  },
  {
    yol: "/sayim",
    metin: "Stok sayımı",
    grup: "Stok",
    ikon: IkonSayim,
    kisa: "Sayım",
    roller: STOK_YETKILI_ROLLER,
  },
  {
    yol: "/transfer",
    metin: "Transferler",
    grup: "Stok",
    ikon: IkonTransfer,
    kisa: "Transfer",
    roller: MALI_MUSAVIR_HARIC,
  },
  {
    yol: "/karantina",
    metin: "Karantina",
    grup: "Stok",
    ikon: IkonKarantina,
    roller: MALI_MUSAVIR_HARIC,
  },
  {
    yol: "/imha",
    metin: "İmha",
    grup: "Stok",
    ikon: IkonImha,
    roller: STOK_YETKILI_ROLLER,
  },
  {
    yol: "/cari",
    metin: "Cari hesaplar",
    grup: "Muhasebe",
    ikon: IkonCari,
    kisa: "Cari",
    roller: MALI,
  },
  {
    yol: "/gelir-gider",
    metin: "Gelir / gider",
    grup: "Muhasebe",
    ikon: IkonGelirGider,
    kisa: "Gelir/gider",
    roller: MALI,
  },
  {
    yol: "/yukleme",
    metin: "Yükleme onayı",
    grup: "Muhasebe",
    ikon: IkonYuklemeOnay,
    roller: YUKLEME_ROLLERI,
    kisa: "Yükleme",
  },
  {
    yol: "/yukleme-mutabakati",
    metin: "Yükleme mutabakatı",
    grup: "Muhasebe",
    ikon: IkonYuklemeMutabakati,
    roller: YUKLEME_ROLLERI,
    // 4,75 rem'e "Yük. mutabakatı" sığmaz ve "Yük. mutab…" diye kırpılır.
    kisa: "Yük. mut.",
  },
  {
    yol: "/nokta",
    metin: "Noktalar",
    grup: "Tanımlar",
    ikon: IkonNokta,
    roller: GOREVLI_VE_MUSAVIR_HARIC,
  },
  {
    yol: "/personel",
    metin: "Personel",
    grup: "Tanımlar",
    ikon: IkonPersonel,
    roller: GOREVLI_VE_MUSAVIR_HARIC,
  },
  {
    yol: "/okul",
    metin: "Okul ayarları",
    grup: "Tanımlar",
    ikon: IkonAyarlar,
    kisa: "Okul",
    roller: GOREVLI_VE_MUSAVIR_HARIC,
  },
];

/**
 * Menü, oturumun rolüyle süzülür. Bu bir GÜVENLİK katmanı değildir — yetki
 * sunucuda zorlanır; amaç, kullanıcıyı açamayacağı bir ekrana davet etmemek.
 */
function useYon(): YonOgesi[] {
  const oturum = aktifOturum(oturumDurumu());
  const rol = oturum?.personel.rol ?? null;
  return YON.filter(
    (y) => !y.roller || (rol !== null && y.roller.includes(rol)),
  );
}

const GRUPLAR: Grup[] = [
  "İzleme",
  "Denetim",
  "Raporlar",
  "Kişi ve kart",
  "Stok",
  "Muhasebe",
  "Tanımlar",
];

export function Uygulama() {
  const istemci = useQueryClient();
  const durum = oturumDurumu();
  const oturum = aktifOturum(durum);
  const [okulEkleniyor, setOkulEkleniyor] = useState(false);
  const abonelik = useQuery({
    queryKey: ["abonelik", oturum?.okulId],
    queryFn: () =>
      apiOku<{
        abonelik: {
          paket_ad: string;
          durum: string;
          bitis_tarihi: string | null;
        } | null;
        uyari: string | null;
      }>("/abonelik"),
    enabled: oturum !== null,
    staleTime: 5 * 60_000,
  });

  // Tema: kullanıcı seçimi sistem tercihini EZER (tasarım sistemi §2.6).
  useEffect(() => temayiUygula(durum.tema), [durum.tema]);

  // 401 -> oturum düştü. Bu olay veri/api.ts'ten gelir; kabuk React bilmeyen
  // katmandan haberi böyle alır.
  useEffect(() => {
    // Depo eylemi getState() ile alınır: böylece effect'in bağımlılığı
    // kararlıdır ve her renderda dinleyici sökülüp takılmaz.
    const dusunce = (olay: Event) => {
      const token = (olay as CustomEvent<{ token: string }>).detail?.token;
      if (!token) return;
      const onceki = aktifOturum(oturumDurumu.getState());
      oturumDurumu.getState().cikis(token);
      if (aktifOturum(oturumDurumu.getState()) !== onceki) istemci.clear();
    };
    window.addEventListener(OTURUM_DUSTU, dusunce);
    const birak = baskaSekmeDinle(() => istemci.clear());
    return () => {
      window.removeEventListener(OTURUM_DUSTU, dusunce);
      birak();
    };
  }, [istemci]);

  if (!oturum || okulEkleniyor) {
    return (
      <Giris {...(oturum ? { vazgec: () => setOkulEkleniyor(false) } : {})} />
    );
  }

  return (
    /* Kabuk EKRANA SABİTTİR, sayfa değil: 23 bölümlü bir panelde kenar çubuğu
       her kaydırmada geri gelmemelidir. Kaydırma yalnızca içerik sayfasındadır.
       Yazdırmada bu kilit AÇILIR — `overflow-hidden` bir ata eleman uzun
       tabloyu tek sayfaya kırpar. */
    <div className="yonetim-kabuk flex h-dvh overflow-hidden bg-app print:h-auto print:overflow-visible">
      <KenarCubugu okulEkle={() => setOkulEkleniyor(true)} />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Dar ekranda kapsam seçicileri kenar çubuğundan bu ince banda çıkar. */}
        <KapsamBandi />

        {oturum.platform && (
          <div
            className="mx-2 mt-2 rounded-md border border-uyari/40 bg-uyari/10 px-3 py-2 text-sm font-satir text-murekkep print:hidden"
            role="status"
          >
            Platform erişimi · {oturum.okulAd}
          </div>
        )}

        {abonelik.data?.uyari && (
          <div
            className="mx-2 mt-2 rounded-md border border-uyari/40 bg-uyari/10 px-3 py-2 text-sm text-murekkep print:hidden"
            role="status"
          >
            {abonelik.data.uyari}
          </div>
        )}

        <main className="yonetim-icerik min-h-0 min-w-0 flex-1 pb-24 md:pb-0 print:overflow-visible print:p-0">
          {/* Sayfa zemini üzerinde ortak kart ve tablo yüzeyleri. */}
          <div className="yonetim-sayfa-paneli h-full overflow-y-auto print:h-auto print:overflow-visible">
            <Routes>
              <Route path="/" element={<Gosterge />} />
              <Route path="/terminal" element={<TerminalSagligi />} />
              <Route path="/mutabakat" element={<Mutabakat />} />
              <Route path="/anomali" element={<Anomali />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/rapor/z" element={<ZRaporu />} />
              <Route path="/rapor/kar-zarar" element={<KarZararRaporu />} />
              <Route path="/rapor/urun-karlilik" element={<UrunKarlilik />} />
              <Route
                path="/rapor/nokta-sorumluluk"
                element={<NoktaSorumluluk />}
              />

              {/* Tanım ve işletme ekranları */}
              <Route path="/kisi" element={<Kisiler />} />
              {VELI_GIRIS_AKTARIM_ROLLERI.includes(oturum.personel.rol) && (
                <Route
                  path="/veli-giris-bilgileri"
                  element={<VeliGirisBilgileri key={oturum.okulId} okulId={oturum.okulId} />}
                />
              )}
              <Route path="/kart" element={<Kartlar />} />
              <Route path="/ice-aktarim" element={<IceAktarim />} />
              <Route path="/urun" element={<Urunler />} />
              <Route path="/stok" element={<Stok />} />
              <Route path="/stok-giris" element={<MalKabul />} />
              <Route path="/stok-giris/:id" element={<MalKabul />} />
              <Route path="/mal-kabul" element={<MalKabulGecmisi />} />
              <Route path="/mal-kabul/:id" element={<MalKabulDetay />} />
              <Route path="/sayim" element={<Sayim />} />
              <Route path="/transfer" element={<Transferler />} />
              <Route path="/karantina" element={<Karantina />} />
              <Route path="/imha" element={<Imha />} />
              <Route path="/cari" element={<Cari />} />
              <Route path="/gelir-gider" element={<GelirGider />} />
              {YUKLEME_ROLLERI.includes(oturum.personel.rol) && (
                <>
                  <Route path="/yukleme" element={<YuklemeOnay />} />
                  <Route
                    path="/yukleme-mutabakati"
                    element={<YuklemeMutabakati />}
                  />
                </>
              )}
              <Route path="/nokta" element={<Noktalar />} />
              <Route path="/personel" element={<Personel />} />
              <Route path="/okul" element={<OkulAyarlari />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>

      <AltSerit />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kapsam seçicileri — okul (KK-801) ve nokta süzgeci
// ---------------------------------------------------------------------------

/** Deterministik çalışma alanı rozeti — okul adından türetilen sabit hue. */
const OKUL_HUE = [
  "var(--etiket-indigo)",
  "var(--etiket-mor)",
  "var(--etiket-camgobegi)",
  "var(--etiket-yesil)",
  "var(--etiket-turuncu)",
  "var(--etiket-pembe)",
] as const;

function okulHuesi(ad: string): string {
  let toplam = 0;
  for (let i = 0; i < ad.length; i += 1) {
    toplam = (toplam * 31 + ad.charCodeAt(i)) >>> 0;
  }
  return OKUL_HUE[toplam % OKUL_HUE.length]!;
}

/**
 * Nokta seçenekleri. AYRI bir kanca: okul seçicisi bunu çağırmaz, aksi halde
 * kenar çubuğunun her satırı gereksiz bir nokta sorgusuna abone olurdu.
 */
function useNoktaSecenekleri() {
  const noktalar = useNoktalar();
  return useMemo(
    () => [
      { deger: "", metin: "Tüm noktalar" },
      ...(noktalar.data?.noktalar ?? [])
        .filter((n) => n.aktif)
        .map((n) => ({
          deger: n.id,
          metin: n.satis_yapar ? n.ad : `${n.ad} (depo)`,
        })),
    ],
    [noktalar.data],
  );
}

/**
 * Çalışma alanı değiştirici — kenar çubuğunun en üstündeki 40px'lik satır.
 * 24px rozet + okul adı + şevron. Görsel olarak bir düğme gibi durur ama
 * altında yerel bir `<select>` vardır: klavye ve ekran okuyucu davranışı
 * tarayıcının kendi kalıbında kalır, taklit bir menü yazılmaz.
 */
function OkulSecici() {
  const istemci = useQueryClient();
  const durum = oturumDurumu();
  const okulDegistir = (okulId: string) => {
    if (okulId === durum.aktifOkulId) return;
    // Önbellek okul değişiminde TAMAMEN atılır: yanlış okulun rakamı bir kare
    // bile görünmemelidir.
    istemci.clear();
    durum.okulSec(okulId);
  };
  const aktif = durum.oturumlar.find((o) => o.okulId === durum.aktifOkulId);
  const ad = aktif?.okulAd ?? "Okul";

  return (
    /* Odak halkası SARMALAYICIDA yaşar: gerçek denetim şeffaf bir `<select>`
       ve kendi halkası görünmez olurdu. `focus-within` klavye kullanıcısına
       satırın tamamını odaklanmış gösterir. */
    <div className="relative flex h-satir items-center gap-2 rounded-md px-1.5 transition-colors duration-defter hover:bg-kagit-2 focus-within:shadow-halka">
      <span
        aria-hidden="true"
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-2xs font-bolum text-dolgu-ustu"
        style={{ background: okulHuesi(ad) }}
      >
        <OkulLogosu ad={ad} token={aktif?.token} boyut={24} />
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-satir text-murekkep">
        {ad}
      </span>
      <span aria-hidden="true" className="text-murekkep-3">
        <IkonSevron boyut={14} />
      </span>
      {/* Görünmez ama TAM BOY: dokunma hedefi satırın tamamıdır. */}
      <label className="gizli-erisilebilir" htmlFor="kenar-okul">
        Okul seç
      </label>
      <select
        id="kenar-okul"
        value={durum.aktifOkulId ?? ""}
        onChange={(e) => okulDegistir(e.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {durum.oturumlar.map((o) => (
          <option key={o.okulId} value={o.okulId}>
            {o.okulAd}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Nokta süzgeci.
 *
 * Süzgeç açıkken bunun GÖRÜNMESİ gerekir: sessizce süzülmüş bir rakam yanlış
 * karar demektir. Açıkken hem ikon hem kenarlık aksana döner, hem de "açık"
 * çipi yazılır — renk tek başına anlam taşımaz.
 */
function NoktaSuzgeci({ id = "kenar-nokta" }: { id?: string }) {
  const durum = oturumDurumu();
  const secenekler = useNoktaSecenekleri();
  const acik = durum.seciliNoktaId !== null;

  return (
    <div className="relative flex items-center">
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute left-2 ${
          acik ? "text-simit-metin" : "text-murekkep-3"
        }`}
      >
        <IkonSuzgec boyut={14} />
      </span>
      <label className="gizli-erisilebilir" htmlFor={id}>
        Nokta süzgeci
      </label>
      <select
        id={id}
        value={durum.seciliNoktaId ?? ""}
        onChange={(e) => durum.noktaSec(e.target.value || null)}
        className={`min-h-kontrol w-full rounded-md border bg-girdi pl-7 pr-2 font-govde text-sm transition-colors duration-defter ease-orta ${
          acik
            ? "border-simit-cizgi text-murekkep"
            : "border-cetvel text-murekkep-2 hover:border-cetvel-koyu"
        }`}
      >
        {secenekler.map((s) => (
          <option key={s.deger} value={s.deger}>
            {s.metin}
          </option>
        ))}
      </select>
    </div>
  );
}

/** 768px altında kapsam seçicilerini taşıyan ince üst bant. */
function KapsamBandi() {
  return (
    <div className="flex shrink-0 items-center gap-2 px-2 py-2 md:hidden print:hidden">
      <div className="min-w-0 flex-1">
        <OkulSecici />
      </div>
      <div className="w-40 shrink-0">
        <NoktaSuzgeci id="bant-nokta" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kenar çubuğu — 240px
// ---------------------------------------------------------------------------

/**
 * Bekleyen iş sayacı. Yalnızca İŞ VARKEN vurgulanır — sıfır bir rozet, gözü
 * her bakışta boşuna çeker. (Kasadaki kuyruk sayacı bunun tersidir ve
 * bilerek: orada sıfırın kendisi bilgidir, bkz. KK-312.)
 */
function useSayac() {
  const ozet = useOzet();
  const bekleyenMutabakat = ozet.data?.bekleyen.mutabakat ?? 0;
  const bekleyenYukleme = ozet.data?.bekleyen.yukleme_onayi ?? 0;
  return (yol: string): number =>
    yol === "/mutabakat"
      ? bekleyenMutabakat
      : yol === "/yukleme"
        ? bekleyenYukleme
        : 0;
}

function KenarCubugu({ okulEkle }: { okulEkle: () => void }) {
  const istemci = useQueryClient();
  const durum = oturumDurumu();
  const oturum = aktifOturum(durum);
  const sayac = useSayac();
  const yon = useYon();

  const sonrakiTema: Record<Tema, Tema> = {
    sistem: "acik",
    acik: "koyu",
    koyu: "sistem",
  };
  const temaMetni: Record<Tema, string> = {
    sistem: "Tema: sistem",
    acik: "Tema: açık",
    koyu: "Tema: koyu",
  };
  // Tema düğmesi ŞU ANKİ durumu gösterir, sıradakini değil: ikon bir vaat
  // değil, bir okumadır.
  const TemaIkonu: Record<Tema, IkonBileseni> = {
    sistem: IkonEkran,
    acik: IkonGunes,
    koyu: IkonAy,
  };
  const AktifTemaIkonu = TemaIkonu[durum.tema];

  /** HesapSis gezinmesi: yumuşak mavi zemin ve mavi etkin öğe. */
  const bagSinifi = ({ isActive }: { isActive: boolean }): string =>
    [
      "yonetim-menu-bagi group flex min-h-kontrol items-center gap-2 rounded-md px-3 text-sm",
      "transition-colors duration-defter ease-orta",
      isActive
        ? "bg-simit-yumusak font-satir text-simit-metin"
        : "text-murekkep-2 hover:bg-kagit-2 hover:text-murekkep",
    ].join(" ");

  return (
    <nav
      aria-label="Panel bölümleri"
      className="yonetim-kenar hidden w-kenar shrink-0 flex-col bg-kenar md:flex print:hidden"
    >
      <div className="yonetim-kenar-marka">
        <YonetimMarkasi />
      </div>
      {/* Çalışma alanı — okul seçici + okul oturumu ekle */}
      <div className="flex items-center gap-1 p-2">
        <div className="min-w-0 flex-1">
          <OkulSecici />
        </div>
        <IkonButon etiket="Okul oturumu ekle" onClick={okulEkle}>
          <IkonArti boyut={16} />
        </IkonButon>
      </div>

      {/* Kapsam süzgeci — gezinmeden ÖNCE durur: hangi noktaya baktığın,
          nereye baktığından önce gelen sorudur. */}
      <div className="px-2 pb-2">
        <NoktaSuzgeci />
      </div>

      {/* Gezinme — içerikten BAĞIMSIZ kaydırılır. Uzun bir tabloyu okurken
          menünün kaybolması yön duygusunu bozar. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {GRUPLAR.filter((grup) => yon.some((y) => y.grup === grup)).map(
          (grup) => (
            <div key={grup} className="mb-4 last:mb-0">
              <p className="yonetim-menu-grubu m-0 mb-1 px-3 text-xs font-satir text-murekkep-2">
                {grup}
              </p>
              <ul className="m-0 flex list-none flex-col gap-px p-0">
                {yon
                  .filter((y) => y.grup === grup)
                  .map((y) => {
                    const adet = sayac(y.yol);
                    return (
                      <li key={y.yol}>
                        <NavLink
                          to={y.yol}
                          end={y.yol === "/"}
                          className={bagSinifi}
                        >
                          {({ isActive }) => (
                            <>
                              <span
                                className={
                                  isActive
                                    ? "text-simit-metin"
                                    : "text-murekkep-3 transition-colors group-hover:text-murekkep-2"
                                }
                              >
                                <y.ikon boyut={16} />
                              </span>
                              <span className="min-w-0 flex-1 truncate">
                                {y.metin}
                              </span>
                              {adet > 0 && (
                                <span className="rakam shrink-0 font-mono text-2xs font-satir text-uyari">
                                  {adet}
                                  <span className="gizli-erisilebilir">
                                    {" "}
                                    bekleyen iş
                                  </span>
                                </span>
                              )}
                            </>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
              </ul>
            </div>
          ),
        )}
      </div>

      {/* Kimlik ve oturum — kenar çubuğunun tabanı */}
      <div className="flex items-center gap-2 border-t border-cetvel-silik p-2">
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-sm font-satir text-murekkep">
            {oturum?.personel.ad} {oturum?.personel.soyad}
          </p>
          <p className="m-0 truncate text-xs text-murekkep-3">
            {oturum?.personel.rol_adi}
          </p>
        </div>
        <IkonButon
          etiket={`${temaMetni[durum.tema]} — değiştirmek için tıklayın`}
          onClick={() => durum.temaAyarla(sonrakiTema[durum.tema])}
        >
          <AktifTemaIkonu boyut={16} />
        </IkonButon>
        <IkonButon
          etiket="Çıkış"
          onClick={() => {
            istemci.clear();
            durum.cikis();
          }}
        >
          <IkonCikis boyut={16} />
        </IkonButon>
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// 768px altı — yatay şerit
// ---------------------------------------------------------------------------

/**
 * Dar ekranda yön şeridi sayfanın altına sabitlenir. Burada ikon etiketin
 * ÜSTÜNDEDİR: dar şeritte yan yana dizilim ya etiketi kırpar ya da tek ekrana
 * üç kalem sığdırır. Yatay kaydırılabilir — içerik alanı daralmasın.
 */
function AltSerit() {
  const sayac = useSayac();
  const yon = useYon();
  const durum = oturumDurumu();
  const istemci = useQueryClient();

  return (
    <nav
      aria-label="Panel bölümleri"
      className="yonetim-alt-serit fixed bottom-0 left-0 right-0 z-10 overflow-x-auto border-t border-cetvel bg-kenar px-2 py-1 md:hidden print:hidden"
    >
      <ul className="m-0 flex list-none gap-1 p-0">
        {yon.map((y) => (
          <li key={y.yol} className="shrink-0">
            <NavLink
              to={y.yol}
              end={y.yol === "/"}
              className={({ isActive }) =>
                [
                  "flex min-h-hedef w-[4.75rem] flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1.5 text-center text-2xs leading-tight",
                  "transition-colors duration-defter ease-orta",
                  isActive
                    ? "bg-simit-yumusak font-satir text-simit-metin"
                    : "text-murekkep-2",
                ].join(" ")
              }
            >
              <span className="relative">
                <y.ikon boyut={18} />
                {sayac(y.yol) > 0 && (
                  <span className="rakam absolute -right-2.5 -top-1.5 min-w-4 rounded-tam border border-uyari-cizgi bg-uyari-yumusak px-1 text-center font-mono text-2xs font-satir leading-4 text-uyari">
                    {sayac(y.yol)}
                  </span>
                )}
              </span>
              <span className="w-full truncate">{y.kisa ?? y.metin}</span>
            </NavLink>
          </li>
        ))}
        {/* Dar ekranda kenar çubuğu tabanı yok; oturum eylemleri şeridin
            sonunda durur. */}
        <li className="ml-1 flex shrink-0 items-center gap-1 border-l border-cetvel pl-2">
          <IkonButon
            etiket="Çıkış"
            onClick={() => {
              istemci.clear();
              durum.cikis();
            }}
          >
            <IkonCikis boyut={16} />
          </IkonButon>
        </li>
      </ul>
    </nav>
  );
}
